import { useTheme } from "next-themes";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../store/AuthContext";
import { getInvestorRequests } from "../services/requests.service";
import { numberWithCommas, separateDecimal, separateNumber, separateNumberIfDecimal } from "../utils/numbers";
import RequestItem from "../components/requestItem";

import Modal from "bootstrap/js/dist/modal";
import { customCancelButton, customModalCancelButton, tabButton, themeBasedPagination, themeBasedSignupFormControl } from "../components/customStyles";
import Pagination from "../components/pagination";
import { customToast } from "../components/toasts";
import toast from "react-hot-toast";
import { updatePaymentProof } from "../services/invest.service";
import { UploadIcon } from "../components/icons";
import { useOutletContext, useSearchParams } from "react-router-dom";
import ImageUpload from "../components/imageUpload";
import { useTranslation } from "react-i18next";
import { useI18n } from "../hooks/useI18n";
import { Tooltip } from "react-tooltip";

const allowedActions = ["PAYMENT-REJECTED"];
const allowedExplicitActions = ["PAYMENT-PENDING", "PAYMENT-REJECTED"];

const Requests = ({ funds, loadingFunds, portfolioFunds, setActiveSubMenu }) => {
  const [tabScrollingLeft, setTabScrollingLeft] = useState(false);
  const [tabScrollingRight, setTabScrollingRight] = useState(false);
  const [activeTabItem, setActiveTabItem] = useState(1);
  const [activePageNumber, setActivePageNumber] = useState(1);
  const [investorRequests, setInvestorRequests] = useState(null);
  const [investorRequestError, setInvestorRequestError] = useState(false);
  const [activeRequestData, setActiveRequestData] = useState(null);
  const [paymentProof, setPaymentProof] = useState(null);
  const [userPaymentProof, setUserPaymentProof] = useState(null);
  const [paymentProofError, setPaymentProofError] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  const [screenSize, setScreenSize] = useState();

  const { theme } = useTheme();
  const { t } = useTranslation();
  const { isRTL } = useI18n();
  const { user } = useAuth();

  const [searchParams] = useSearchParams();
  // const searchTerm = searchParams.get("search");
  // const searchQuery = searchParams.get("query");
  const purchaseSearchModal = searchParams.get("modal");
  const purchaseSearchSymbol = searchParams.get("symbol");

  const tabsScrollRef = useRef(null);

  const paymentProofModal = useRef(null);
  const paymentProofModalInstance = useRef(null);

  const requestDetailsModal = useRef(null);
  const requestDetailsInstanceModal = useRef(null);

  const fileUploadRef = useRef(null);

  const abortRef = useRef(null);

  const requestsWithFundName = useMemo(() => {
    if (loadingFunds || !funds || !investorRequests?.docs?.length) return [];

    return investorRequests.docs.map((request) => {
      const match = funds.find((s) => s.symbol_code === request.fund_code);
      const secondMatch = funds.find((s) => s.symbol_code === request.convert_fund_code);
      return { ...request, fund_name: match?.fund_name?.trim() ?? "", convert_fund_name: secondMatch?.fund_name?.trim() ?? "" };
    });
  }, [investorRequests?.docs, funds, loadingFunds]);

  // let requestsWithFundName = investorRequests?.docs || null;

  // if (investorRequests?.docs && investorRequests?.docs.length !== 0) {
  //   const updatedRequests = investorRequests?.docs.map((request) => {
  //     let fundName = "";

  //     sharedFundsList.forEach((fund) => {
  //       if (fund.fundCode === request.fund_code) {
  //         fundName = fund.fundName.trim() || "";
  //       }
  //     });

  //     return { ...request, fund_name: fundName };
  //   });

  //   requestsWithFundName = updatedRequests;
  // }

  const handleLeftScroll = () => {
    tabsScrollRef.current.scrollLeft -= 60;
  };

  const handleRightScroll = () => {
    tabsScrollRef.current.scrollLeft += 60;
  };

  const handleFileUpload = (file) => {
    if (!file) {
      setUserPaymentProof(null);
      return;
    }
    // if (e.target.files && e.target.files.length > 0) {
    //   const file = e.target.files[0];

    const allowedExts = ["png", "jpg", "jpeg"];

    if (!allowedExts.includes(file.name.split(".").pop().toLowerCase())) {
      toast((t) => customToast("Invalid File Type", "error", t), {
        duration: 3000,
        removeDelay: 1000,
        style: {
          padding: "0.75rem",
          margin: "0",
          minWidth: "200px",
          backgroundColor: "rgb(255,255,255)",
        },
      });
      return;
    }

    setUserPaymentProof(file);
    // }
  };

  const handleTabChange = (id) => {
    setActivePageNumber(1);
    setActiveTabItem(id);
  };

  const functionsPassed = {
    separate: separateDecimal,
    commas: numberWithCommas,
  };

  const togglePaymentProofModal = (data) => {
    if (data) {
      setActiveRequestData(data);

      if (data.payment_proof) {
        setPaymentProof(data.payment_proof);
      } else {
        setPaymentProof(null);
      }
    }

    paymentProofModalInstance.current?.toggle();
  };

  const toggleDetailsModal = (data) => {
    if (data) {
      setActiveRequestData(data);
    }

    requestDetailsInstanceModal.current?.toggle();
  }

  const updatePaymentProofHandler = async () => {
    if (!userPaymentProof) {
      toast((t) => customToast("You need to upload proof first.", "error", t), {
        duration: 3000,
        removeDelay: 1000,
        style: {
          padding: "0.75rem",
          margin: "0",
          minWidth: "200px",
          backgroundColor: "rgb(255,255,255)",
        },
      });
      return;
    }

    const args = {
      email: activeRequestData?.requested_by_email,
      folio_no: activeRequestData?.folio_no,
      fund_code: activeRequestData?.fund_code,
      request_id: activeRequestData?.request_id,
      bank_account_code: activeRequestData?.bank_account_code,
      payment_method: activeRequestData?.payment_method,
      payment_proof_file: userPaymentProof,
    };

    try {
      setIsUploading(true);

      await new Promise((res) => setTimeout(() => res(), 1000));

      const response = await updatePaymentProof(args);

      if (!response.error) {
        toast((t) => customToast(response.message, "success", t), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            minWidth: "200px",
            backgroundColor: "rgb(255,255,255)",
          },
        });

        togglePaymentProofModal(null);

        try {
          const controller = new AbortController();

          setIsFetching(true);

          const { email, folio_no } = user.data;

          let request_status = "";

          switch (activeTabItem) {
            case 1:
              break;
            case 2:
              request_status = "PAYMENT-PENDING";
              break;
            case 3:
              request_status = "SUBMITTED";
              break;
            case 4:
              request_status = "ACCEPTED";
              break;
            case 5:
              request_status = "REJECTED";
              break;
            case 6:
              request_status = "PAYMENT-REJECTED";
              break;
          }

          const response = await getInvestorRequests({ email, folio_no, request_status, activePageNumber, signal: controller.signal });

          if (!response.error) {
            setInvestorRequests(response.data.data);
          } else {
            setInvestorRequests([]);
          }
        } finally {
          setIsFetching(false);
        }
      } else {
        toast((t) => customToast("Failed to update request", "error", t), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            minWidth: "200px",
            backgroundColor: "rgb(255,255,255)",
          },
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const conversionRequest = useMemo(() => {
    if (!activeRequestData && !(activeRequestData?.request_type === "conversionofunits")) return null;

    return {
      units: separateNumber(activeRequestData?.units_requested || "0"),
      amount: separateNumber(activeRequestData?.gross_amount_limit || "0"),
      price: separateNumberIfDecimal(activeRequestData?.nav_at_request || "0", { format: true, digits: 4, method: 'round' }),
      convert_units: separateNumber(activeRequestData?.convert_units_requested || "0"),
      convert_price: separateNumberIfDecimal(activeRequestData?.convert_nav_at_request || "0", { format: true, digits: 4, method: 'round' })
    }
  }, [activeRequestData]);


  useEffect(() => {
    const resetPaymentProof = () => {
      setPaymentProof(null);
      setUserPaymentProof(null);
      setActiveRequestData(null);
      if (fileUploadRef.current) {
        fileUploadRef.current.value = "";
      }
    };

    if (paymentProofModal.current) {
      paymentProofModalInstance.current = new Modal(paymentProofModal.current, { backdrop: true, keyboard: false });

      paymentProofModal.current.addEventListener("hidden.bs.modal", resetPaymentProof);
    }

    if (requestDetailsModal.current) {
      requestDetailsInstanceModal.current = new Modal(requestDetailsModal.current, { backdrop: true, keyboard: false });

      // requestDetailsModal.current.addEventListener("hidden.bs.modal", resetPaymentProof);
    }

    const tabsRowScrollHandler = () => {
      if (tabsScrollRef.current.scrollLeft > 0) {
        setTabScrollingLeft(true);
      } else {
        setTabScrollingLeft(false);
      }

      if (Math.ceil(tabsScrollRef.current.clientWidth + tabsScrollRef.current.scrollLeft) >= tabsScrollRef.current.scrollWidth) {
        setTabScrollingRight(false);
      } else {
        setTabScrollingRight(true);
      }
    };

    const resizeScreen = () => {
      setScreenSize(window?.innerWidth);
      if (tabsScrollRef.current) {
        tabsRowScrollHandler();
      }
    };

    resizeScreen();
    if (tabsScrollRef.current) {
      tabsRowScrollHandler();
    }

    window.addEventListener("resize", resizeScreen);
    tabsScrollRef.current?.addEventListener("scroll", tabsRowScrollHandler);

    return () => {
      window.removeEventListener("resize", resizeScreen);
      tabsScrollRef.current?.removeEventListener("scroll", tabsRowScrollHandler);
      paymentProofModal.current?.removeEventListener("hidden.bs.modal", resetPaymentProof);

      paymentProofModalInstance.current?.dispose();
      requestDetailsInstanceModal.current?.dispose();
    };
  }, []);

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const waitAndUpdate = async () => {
      setIsFetching(true);

      await new Promise((res) => setTimeout(() => res(), 750));

      try {
        const { email, folio_no } = user.data;

        let request_status = "";

        switch (activeTabItem) {
          case 1:
            break;
          case 2:
            request_status = "PAYMENT-PENDING";
            break;
          case 3:
            request_status = "SUBMITTED";
            break;
          case 4:
            request_status = "ACCEPTED";
            break;
          case 5:
            request_status = "REJECTED";
            break;
          case 6:
            request_status = "PAYMENT-REJECTED";
            break;
        }

        const response = await getInvestorRequests({ email, folio_no, request_status, activePageNumber, signal: controller.signal });

        if (!response.error) {
          setInvestorRequests(response.data.data);
        } else {
          setInvestorRequests([]);
        }
      } finally {
        setIsFetching(false);
      }
    };

    if (user) {
      waitAndUpdate();
    }

    return () => {
      controller.abort();
    };
  }, [activeTabItem, activePageNumber]);

  useEffect(() => {
    setPaymentProofError(false);
  }, [activeRequestData]);

  useEffect(() => {
    if (portfolioFunds && portfolioFunds.length !== 0 && purchaseSearchModal) {
      const currentFund = portfolioFunds.find((item) => item.fund_code === purchaseSearchSymbol);

      if (currentFund) {
        setActiveSubMenu('portfolio');
      }
    }
  }, [portfolioFunds, purchaseSearchModal]);

  const tabMenuItems = [
    {
      id: 1,
      title: t('requests.tabs.all'),
    },
    {
      id: 2,
      title: t('requests.tabs.pending'),
    },
    {
      id: 3,
      title: t('requests.tabs.submitted'),
    },
    {
      id: 4,
      title: t('requests.tabs.accepted'),
    },
    {
      id: 5,
      title: t('requests.tabs.rejected'),
    },
    {
      id: 6,
      title: t('requests.tabs.payment'),
    },
  ];

  return (
    <div className="col-12 p-0 px-sm-1" style={{
      borderRadius: "20px",
      // marginTop: '12px',
      // backgroundColor: theme === "dark" ? "var(--theme-dark-one)" : "var(--theme-light-one)",
            height:
              screenSize <= 575
                ? "calc(100dvh - 212px)"
                : screenSize <= 767 && screenSize >= 576
                  ? "calc(100dvh - 265px)"
                  : screenSize <= 991 && screenSize >= 768
                    ? "calc(100dvh - 265px)"
                    : "calc(100dvh - 192px)",
    }}>
      {/*
       *	TODO:   Make the tab menu responsive
       * 	GIVEN:  Large already done
       */}

      {/* -- TABS -- */}
      <div className='row w-100 mx-auto align-items-center justify-content-center position-relative p-2'>
        <div
          className='col-auto p-0 position-relative'
          style={{
            overflow: "hidden",
            // borderTopLeftRadius: '20px',
            // borderTopRightRadius: '20px'
          }}
        >
          {tabScrollingLeft ? (
            <button
              className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
              onClick={() => handleLeftScroll()}
              style={{
                top: "0px",
                left: "3px",
                color: "var(--theme-light-secondary)",
                padding: "2px",
                background: theme === "dark" ? "rgb(45, 54, 70)" : "rgb(217, 230, 248)",
                boxShadow:
                  theme === "dark"
                    ? "rgb(45, 54, 70) 0px 0px 12px 14px, rgb(45, 54, 70) -13px 0px 4px 8px"
                    : "rgb(217, 230, 248) 0px 0px 12px 14px, rgb(217, 230, 248) -12px 0px 0px 6px",
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width={30}
                height={30}
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-left'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M15 6l-6 6l6 6' />
              </svg>
            </button>
          ) : (
            ""
          )}

          {tabScrollingRight ? (
            <button
              className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
              onClick={() => handleRightScroll()}
              style={{
                top: "0px",
                right: "3px",
                color: "var(--theme-light-secondary)",
                padding: "2px",
                background: theme === "dark" ? "rgb(67, 53, 70)" : "rgb(243, 226, 246)",
                boxShadow:
                  theme === "dark"
                    ? "rgb(67, 53, 70) 0px 0px 12px 14px, rgb(67, 53, 70) 13px 0px 4px 8px"
                    : "rgb(243, 226, 246) 0px 0px 12px 14px, rgb(243, 226, 246) 13px 0px 4px 8px",
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width={30}
                height={30}
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-right'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M9 6l6 6l-6 6' />
              </svg>
            </button>
          ) : (
            ""
          )}

          <div
            className='tabs-row row w-100 mx-auto align-items-center gap-2 flex-nowrap overflow-x-auto mw-100 pe-2'
            ref={tabsScrollRef}
            style={{
              scrollbarWidth: "none",
              // borderTopRightRadius: '20.4px',
              // borderTopLeftRadius: '20.4px'
            }}
          >
            {tabMenuItems.map((item, index) => {
              // const numberOfTabs = tabMenuItems.length;
              // // const isPortfolio = activeMenuItem === 'portfolio';

              // let appliedMargin;

              // if (numberOfTabs === 1) {
              // 	appliedMargin = "ms-auto"
              // 	// } else if (isPortfolio) {
              // 	// 	appliedMargin = "ms-auto"
              // } else {
              // 	if (index === 0) {
              // 		appliedMargin = 'ms-auto';
              // 	} else {
              // 		appliedMargin = 'm-0';
              // 	}
              // }

              return (
                <>
                  <div className={`col-auto p-0`} key={item.id}>
                    {/* <button className={`btn btn-primary border-0 ${currentActiveTab() === tabItem.path ? 'active' : ''}`} to={tabItem.path} type="button" style={{ */}
                    <button
                      className={`btn${activeTabItem === item.id ? " active" : ""}`}
                      type='button'
                      onClick={() => handleTabChange(item.id)}
                      style={{
                        lineHeight: "1",
                        fontSize: "0.8rem",
                        padding: "10px 16px 11px",
                        minWidth: "62px",
                        borderRadius: "99px",
                        ...tabButton(theme)
                      }}
                    >
                      {item.title}
                    </button>
                  </div>
                </>
              );
            })}
          </div>
        </div>
      </div>

      {/* -- MAIN CONTENT -- */}
      <div style={{
        // width: screenSize >= 576 ? '100%' : '552px',
        width: '100%',
        overflowX: screenSize >= 576 ? 'none' : 'auto',
        scrollbarWidth: 'none'
      }}>
        <div
          className={`add-custom-scroll${theme === "dark" ? "-dark" : ""} position-relative d-flex mx-auto flex-column justify-content-start flex-nowrap`}
          style={{
            padding: screenSize <= 575 ? "8px" : "4px 10px 10px 12px",
            // paddingTop: '10px',
            // maxWidth: '100%',
            width: screenSize >= 576 ? '100%' : '552px',
            borderBottomLeftRadius: screenSize >= 576 ? "19px" : "",
            borderBottomRightRadius: screenSize >= 576 ? "19px" : "",
            rowGap: "8px",
            height:
              screenSize <= 575
                ? "calc(100dvh - 328px)"
                : screenSize <= 767 && screenSize >= 576
                  ? "calc(100dvh - 372px)"
                  : screenSize <= 991 && screenSize >= 768
                    ? "calc(100dvh - 372px)"
                    : "calc(100dvh - 300px)",
            overflowY: "auto",
            overflowX: 'hidden',
            scrollbarWidth: screenSize >= 576 ? "" : "none"
            // overflowX: screenSize >= 576 ? "" : "auto",
          }}
        >
          {(!isFetching && !loadingFunds) ? (
            requestsWithFundName?.length ? (
              requestsWithFundName?.map((item, index) => {
                return (
                  <>
                    <RequestItem
                      key={index}
                      data={item}
                      t={t}
                      action={activeTabItem === 4 ? true : false}
                      toggleProofModal={togglePaymentProofModal}
                      toggleDetailsModal={toggleDetailsModal}
                      utils={functionsPassed}
                    />
                    {index <= investorRequests?.docs?.length && (
                      <div className='col-12 p-0' key={String.fromCharCode(65 + index) + index}>
                        <hr
                          className='m-0 mx-auto'
                          style={{
                            maxWidth: "calc(100% - 10px)",
                            color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary)" : "var(--theme-light-tertiary)"},0.45)`,
                          }}
                        />
                      </div>
                    )}
                  </>
                );
              })
            ) : (
              <div
                className='w-100 position-absolute start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
                style={{
                  color: theme === "light" ? "rgb(0,0,0)" : "rgb(255,255,255)",
                  top: 'calc(50%)'
                }}
              >
                <div
                  className=''
                  role='status'
                  style={{
                    fontSize: "1.2rem",
                    color: theme === "dark" ? "rgb(200,200,200)" : "rgb(200,200,200)",
                  }}
                >
                  {t("app.messages.no.record")}
                </div>
              </div>
            )
          ) : (
            <div
              className='w-100 position-absolute start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
              style={{
                color: theme === "light" ? "rgb(0,0,0)" : "rgb(255,255,255)",
                top: 'calc(50%)'
              }}
            >
              <div
                className=''
                role='status'
                style={{
                  fontSize: "1.2rem",
                  fontWeight: '300'
                }}
              >
                {t("app.loading.requests")}
              </div>
              <div
                className='spinner-border'
                style={{
                  fontSize: "0.7rem",
                  height: "24px",
                  width: "24px",
                  color: theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                }}
              >
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* -- PAYMENT PROOF - MODAL -- */}
      <div className='modal fade' id='paymentProofModal' tabIndex='-1' aria-labelledby='paymentProofModal' aria-hidden='true' ref={paymentProofModal}>
        <div
          className='modal-dialog modal-dialog-centered m-2 mx-md-auto'
          style={{
            ["--bs-modal-width"]: "600px",
          }}
        >
          <div
            className='modal-content position-relative'
            style={{
              backgroundColor: theme === "light" ? "rgb(240, 240, 240)" : "rgb(50, 50, 50)",
              border: "none",
            }}
          >
            <button
              type='button'
              className='btn-close p-0 position-absolute d-flex align-items-center justify-content-center'
              aria-label='Close'
              style={{
                zIndex: "100",
                top: "12px",
                right: isRTL ? "" : "12px",
                left: isRTL ? "12px" : "",
                marginLeft: "0",
                marginRight: ".15rem",
                color: theme === 'dark' ? "rgb(255,255,255)" : "rgb(138, 134, 134)",
                background: "none",
                height: "32px",
                width: "32px",
                ["--bs-btn-close-opacity"]: "0.65",
                ["--bs-btn-close-hover-opacity"]: "0.85",
              }}
              onClick={togglePaymentProofModal}
            >
              <svg xmlns='http://www.w3.org/2000/svg' height={18} width={18} stroke='currentColor' fill='currentColor' viewBox='0 0 16 16'>
                <path d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414'></path>
              </svg>
            </button>

            <hr className='m-0' />

            <div className='modal-body p-0'>
              <div
                className=''
                style={{
                  padding: allowedExplicitActions.includes(activeRequestData?.request_status) ? "1rem" : "1rem 1rem 0 1rem",
                }}
              >
                <h1
                  className={`${allowedExplicitActions.includes(activeRequestData?.request_status) ? "" : "text-center"}`}
                  style={{
                    color: theme === 'dark' ? "rgb(224, 232, 240)" : "rgb(45, 107, 183)",
                    fontSize: "1.4rem",
                    fontWeight: "500",
                    lineHeight: "1.1",
                  }}
                >
                  {`${allowedActions.includes(activeRequestData?.request_status) ? `${t("modals.common.update")} ` : activeRequestData?.request_status === "PAYMENT-PENDING" ? `${t("modals.common.upload")} ` : ""}${t("modals.request.title")}`}
                </h1>
                {allowedExplicitActions.includes(activeRequestData?.request_status) ? (
                  <div
                    className=''
                    style={{
                      fontSize: ".96rem",
                      color: "rgb(150,150,150)",
                    }}
                  >
                    {/* {`Please ${allowedActions.includes(activeRequestData?.request_status) ? "update" : "upload"} the proof of the payment for this request.` + " "} */}
                    {t("modals.request.subtitle", { type: allowedActions.includes(activeRequestData?.request_status) ? t("modals.request.update") : t("modals.request.upload") })}
                    <span className='ms-2' style={{ color: "var(--theme-light-secondary)" }}>
                      {"(JPG, PNG, JPEG, WEBP)"}
                    </span>
                  </div>
                ) : (
                  ""
                )}
              </div>

              {activeRequestData?.request_status === "PAYMENT-PENDING" ? (
                <>
                  <div className='p-3'>
                    <ImageUpload onFileChange={handleFileUpload} allowedFileTypes={["image/jpeg", "image/png", "image/jpg", "image/webp"]} />
                  </div> 
                </>
              ) : allowedActions.includes(activeRequestData?.request_status) ? (
                <>
                  <div className='p-3 pb-1'>
                    <ImageUpload initialImage={paymentProof ? paymentProof : null} onFileChange={handleFileUpload} allowedFileTypes={["image/jpeg", "image/png", "image/jpg", "image/webp" ]} />
                  </div>
                </>
              ) : (
                <div className='p-3 pb-1'>
                  <ImageUpload initialImage={paymentProof ? paymentProof : null} onFileChange={handleFileUpload} disabled={true} />
                </div>
              )}
            </div>

            <div
              className='modal-footer border-0 flex-nowrap'
              style={{
                justifyContent: allowedExplicitActions.includes(activeRequestData?.request_status) ? "space-between" : "center",
              }}
            >
              <button
                type='button'
                className='btn border-0 px-4 pb-2'
                onClick={() => togglePaymentProofModal()}
                style={{
                  ...customModalCancelButton,
                  minWidth: "94px",
                }}
              >
                {t("modals.common.close")}
              </button>
              {allowedExplicitActions.includes(activeRequestData?.request_status) ? (
                <button
                  type='button'
                  className='btn btn-primary border-0 px-2 pb-2'
                  disabled={isUploading}
                  onClick={updatePaymentProofHandler}
                  style={{
                    minWidth: "94px",
                  }}
                >
                  {isUploading ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm'
                        aria-hidden='true'
                        style={{
                          fontSize: "0.85rem",
                          height: screenSize >= 576 ? "15px" : "14px",
                          width: screenSize >= 576 ? "15px" : "14px",
                        }}
                      ></span>
                    </>
                  ) : (
                    t("modals.common.submit")
                  )}
                </button>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>

      {/* -- REQUEST DETAILS - MODAL -- */}
      <div className='modal fade' id='requestDetailsModal' tabIndex='-1' aria-labelledby='requestDetailsModal' aria-hidden='true' ref={requestDetailsModal}>
        <div
          className='modal-dialog modal-dialog-centered'
          style={{
            ["--bs-modal-width"]: "600px",
            margin: screenSize >= 615 ? "auto" : "auto 8px"
          }}
        >
          <div
            className='modal-content position-relative'
            style={{
              backgroundColor: theme === "light" ? "rgb(240, 240, 240)" : "rgb(50, 50, 50)",
              border: "none",
            }}
          >
            <button
              type='button'
              className='btn-close p-0 position-absolute d-flex align-items-center justify-content-center'
              aria-label='Close'
              style={{
                zIndex: "100",
                top: "12px",
                right: isRTL ? "" : "12px",
                left: isRTL ? "12px" : "",
                marginLeft: "0",
                marginRight: ".15rem",
                color: theme === 'dark' ? "rgb(255,255,255)" : "rgb(138, 134, 134)",
                background: "none",
                height: "32px",
                width: "32px",
                ["--bs-btn-close-opacity"]: "0.65",
                ["--bs-btn-close-hover-opacity"]: "0.85",
              }}
              onClick={() => {
                if (requestDetailsModal.current) {
                  requestDetailsInstanceModal.current?.toggle();
                }
              }}
            >
              <svg xmlns='http://www.w3.org/2000/svg' height={18} width={18} stroke='currentColor' fill='currentColor' viewBox='0 0 16 16'>
                <path d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414'></path>
              </svg>
            </button>

            <hr className='m-0' />

            <div className='modal-body p-0'>
              <div
                className='mb-3'
                style={{
                  padding: "1rem 1rem 0 1rem",
                }}
              >
                <h1
                  className={"text-center"}
                  style={{
                    color: theme === 'dark' ? "rgb(224, 232, 240)" : "rgb(45, 107, 183)",
                    fontSize: "1.4rem",
                    fontWeight: "500",
                    lineHeight: "1.1",
                  }}
                >
                  {t("modals.request.details")}
                </h1>
              </div>
              <div className="p-2 p-sm-3">
                  <div
                    className="mb-2"
                    style={{
                      fontSize: "20px",
                      color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                      letterSpacing: ".02em",
                      lineHeight: "1.1",
                    }}
                  >
<svg  xmlns="http://www.w3.org/2000/svg" width={24}  height={24}  viewBox="0 0 24 24"  fill="currentColor"  className="icon icon-tabler icons-tabler-filled icon-tabler-square-rounded-arrow-up me-2"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 2c-.218 0 -.432 .002 -.642 .005l-.616 .017l-.299 .013l-.579 .034l-.553 .046c-4.785 .464 -6.732 2.411 -7.196 7.196l-.046 .553l-.034 .579c-.005 .098 -.01 .198 -.013 .299l-.017 .616l-.004 .318l-.001 .324c0 .218 .002 .432 .005 .642l.017 .616l.013 .299l.034 .579l.046 .553c.464 4.785 2.411 6.732 7.196 7.196l.553 .046l.579 .034c.098 .005 .198 .01 .299 .013l.616 .017l.642 .005l.642 -.005l.616 -.017l.299 -.013l.579 -.034l.553 -.046c4.785 -.464 6.732 -2.411 7.196 -7.196l.046 -.553l.034 -.579c.005 -.098 .01 -.198 .013 -.299l.017 -.616l.005 -.642l-.005 -.642l-.017 -.616l-.013 -.299l-.034 -.579l-.046 -.553c-.464 -4.785 -2.411 -6.732 -7.196 -7.196l-.553 -.046l-.579 -.034a28.058 28.058 0 0 0 -.299 -.013l-.616 -.017l-.318 -.004l-.324 -.001zm-.148 5.011l.058 -.007l.09 -.004l.075 .003l.126 .017l.111 .03l.111 .044l.098 .052l.104 .074l.082 .073l4 4a1 1 0 0 1 -1.32 1.497l-.094 -.083l-2.293 -2.292v5.585a1 1 0 0 1 -1.993 .117l-.007 -.117v-5.585l-2.293 2.292a1 1 0 0 1 -1.32 .083l-.094 -.083a1 1 0 0 1 -.083 -1.32l.083 -.094l4 -4a.927 .927 0 0 1 .112 -.097l.11 -.071l.114 -.054l.105 -.035l.118 -.025z" fill="currentColor" strokeWidth={0} 
                        style={{
                          color: theme === "dark" ? "#b2cbdb" : "var(--theme-light-secondary)",
                        }}
/></svg>
                    Converted From
                  </div>
                  
                  <div className={`goal-details-card mb-3`} style={{
                    minHeight: '200px'
                  }}>
                    <div className="row w-100 mx-auto row-gap-3">
                      <div className="col-4 p-0">
                        <div className="" style={{
                          fontSize: '15px',
                          color: theme === 'dark' ? 'rgba(255,255,255,.6)' : ''
                        }}>
                          Fund
                        </div>
                        <div className="" style={{
                          fontSize: '22px',
                          fontWeight: '500',
                          letterSpacing: '.02em',
                          color: theme === 'dark' ? 'var(--theme-light-secondary)': "",
                        }}>
                          {activeRequestData?.fund_code}
                        </div>
                        <div
                          data-tooltip-id={"tt-simple"}
                          data-tooltip-content={activeRequestData?.fund_name}
                        className="text-truncate" style={{
                          cursor: 'pointer',
                          fontSize: '13px',
                          lineHeight: '1.1',
                          maxWidth: '70%'
                        }}>
                          {activeRequestData?.fund_name}
                        </div>
                            <Tooltip
                              // anchorSelect={"asd"}
                              id={"tt-simple"}
                              place='bottom'
                              // offset={12}
                              // delayShow={120}
                              className='my-rt'
                              classNameArrow='my-rt-arrow'
                              border='1px solid rgba(255,255,255,0.12)'
                              arrowSize={8}
                              opacity={0.95}
                            />
                      </div>
                      <div className="col-4 p-0">
                        <div className="" style={{
                          fontSize: '15px',
                          color: theme === 'dark' ? 'rgba(255,255,255,.6)' : ''
                        }}>
                          Price
                        </div>
                        <div className="" dir="ltr" style={{
                          fontSize: '22px',
                          fontWeight: '500',
                          letterSpacing: '.02em',
                          color: theme === 'dark' ? 'var(--theme-light-secondary)': ""
                        }}>
                          <span dir="ltr" className="" style={{ marginRight: '5px', fontSize: '15px', fontWeight: '400', color: theme === 'dark' ? '#b2cbdb' : '' }}>
                            {t("app.fund.rupee")}
                          </span>
                          {numberWithCommas(conversionRequest?.price?.whole)}
                          {conversionRequest?.price?.hasDecimal ? "." : ""}
                          {conversionRequest?.price?.hasDecimal ? (
                            <span className="" style={{ fontSize: '16px' }}>
                              {conversionRequest?.price?.decimal}
                            </span>
                          ) : ""}
                        </div>
                        <div className="text-truncate" style={{
                          fontSize: '13px',
                          lineHeight: '1.1'
                        }}>
                          {" "}
                        </div>
                      </div>
                      <div className="col-4 p-0">
                        <div className="" style={{
                          fontSize: '15px',
                          color: theme === 'dark' ? 'rgba(255,255,255,.6)' : ''
                        }}>
                          Conversion Units
                        </div>
                        <div className="" style={{
                          fontSize: '22px',
                          fontWeight: '500',
                          letterSpacing: '.02em',
                          color: theme === 'dark' ? 'var(--theme-light-secondary)': ""
                        }}>
                          {numberWithCommas(conversionRequest?.units?.whole)}
                          {conversionRequest?.units?.hasDecimal ? "." : ""}
                          {conversionRequest?.units?.hasDecimal ? (
                            <span className="" style={{ fontSize: '16px' }}>
                              {conversionRequest?.units?.decimal}
                            </span>
                          ) : ""}
                          <span dir="ltr" className="" style={{ marginLeft: '5px', fontSize: '15px', fontWeight: '400', color: theme === 'dark' ? '#b2cbdb' : '' }}>
                            {t("app.fund.units")}
                          </span>
                        </div>
                        <div className="text-truncate" style={{
                          fontSize: '13px',
                          lineHeight: '1.1'
                        }}>
                          {" "}
                        </div>
                      </div>
                      <div className="col-4 p-0">
                        <div className="" style={{
                          fontSize: '15px',
                          color: theme === 'dark' ? 'rgba(255,255,255,.6)' : ''
                        }}>
                          Conversion Amount
                        </div>
                        <div className="" style={{
                          fontSize: '22px',
                          fontWeight: '500',
                          letterSpacing: '.02em',
                          color: theme === 'dark' ? 'var(--theme-light-secondary)': ""
                        }}>
                          <span dir="ltr" className="" style={{ marginRight: '5px', fontSize: '15px', fontWeight: '400', color: theme === 'dark' ? '#b2cbdb' : '' }}>
                            {t("app.fund.rupee")}
                          </span>
                          {numberWithCommas(conversionRequest?.amount?.whole)}
                          {conversionRequest?.amount?.hasDecimal ? "." : ""}
                          {conversionRequest?.amount?.hasDecimal ? (
                            <span className="" style={{ fontSize: '16px' }}>
                              {conversionRequest?.amount?.decimal}
                            </span>
                          ) : ""}
                        </div>
                        <div className="text-truncate" style={{
                          fontSize: '13px',
                          lineHeight: '1.1'
                        }}>
                          {" "}
                        </div>
                      </div>
                    </div>
                    {/* <div className='d-flex align-items-center justify-content-start flex-nowrap gap-2 mb-4'>
                    </div>
                    <div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          Portfolio Allocated
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {goalData.presentValue && goalData.presentValue.length !== 0 ? numberWithCommas(fmtContribution.whole) : ""}
                          {"."}
                          <span style={{ fontSize: "13px" }}>{fmtContribution.decimal}</span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          Monthly Induction
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          <span className='me-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            Rs.
                          </span>
                          {goalData.monthly_induction_nominal && goalData.monthly_induction_nominal.length !== 0 ? numberWithCommas(fmtPayment.whole) : ""}
                          {"."}
                          <span style={{ fontSize: "13px" }}>{fmtPayment.decimal}</span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          Expected Return
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          {goalData.portfolioGrowthRate && goalData.portfolioGrowthRate.length !== 0 ? goalData.portfolioGrowthRate : ""}
                          <span className='ms-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            %
                          </span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          {"Inflation (yearly)"}
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          {goalData.inflationRate !== null ? goalData.inflationRate : ""}
                          <span className='ms-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            %
                          </span>
                        </div>
                      </div>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div
                          style={{
                            fontSize: "",
                          }}
                        >
                          Duration
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "18px",
                            color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                          }}
                        >
                          {goalDuration && goalDuration.length !== 0 ? numberWithCommas(goalDuration) : ""}
                          <span className='ms-1' style={{ fontSize: "12px", color: theme === "dark" ? "rgb(108, 117, 125)" : "" }}>
                            years
                          </span>
                        </div>
                      </div>
                    </div> */}
                  </div>
                  <div
                    className="mb-2"
                    style={{
                      fontSize: "20px",
                      color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                      letterSpacing: ".02em",
                      lineHeight: "1.1",
                    }}
                  >
<svg  xmlns="http://www.w3.org/2000/svg"  width={24}  height={24}  viewBox="0 0 24 24"  fill="currentColor"  className="icon icon-tabler icons-tabler-filled icon-tabler-square-rounded-arrow-down me-2"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 2c-.218 0 -.432 .002 -.642 .005l-.616 .017l-.299 .013l-.579 .034l-.553 .046c-4.785 .464 -6.732 2.411 -7.196 7.196l-.046 .553l-.034 .579c-.005 .098 -.01 .198 -.013 .299l-.017 .616l-.004 .318l-.001 .324c0 .218 .002 .432 .005 .642l.017 .616l.013 .299l.034 .579l.046 .553c.464 4.785 2.411 6.732 7.196 7.196l.553 .046l.579 .034c.098 .005 .198 .01 .299 .013l.616 .017l.642 .005l.642 -.005l.616 -.017l.299 -.013l.579 -.034l.553 -.046c4.785 -.464 6.732 -2.411 7.196 -7.196l.046 -.553l.034 -.579c.005 -.098 .01 -.198 .013 -.299l.017 -.616l.005 -.642l-.005 -.642l-.017 -.616l-.013 -.299l-.034 -.579l-.046 -.553c-.464 -4.785 -2.411 -6.732 -7.196 -7.196l-.553 -.046l-.579 -.034a28.058 28.058 0 0 0 -.299 -.013l-.616 -.017l-.318 -.004l-.324 -.001zm0 5a1 1 0 0 1 .993 .883l.007 .117v5.585l2.293 -2.292a1 1 0 0 1 1.32 -.083l.094 .083a1 1 0 0 1 .083 1.32l-.083 .094l-4 4a1.008 1.008 0 0 1 -.112 .097l-.11 .071l-.114 .054l-.105 .035l-.149 .03l-.117 .006l-.075 -.003l-.126 -.017l-.111 -.03l-.111 -.044l-.098 -.052l-.092 -.064l-.094 -.083l-4 -4a1 1 0 0 1 1.32 -1.497l.094 .083l2.293 2.292v-5.585a1 1 0 0 1 1 -1z" fill="currentColor" strokeWidth={0} style={{
                          color: theme === "dark" ? "#b2cbdb" : "var(--theme-light-secondary)",
                        }}/></svg>
                    Converted To
                  </div>
                  <div className={`goal-details-card`} style={{
                    minHeight: '120px'
                  }}>
                    <div className="row w-100 mx-auto row-gap-3">
                      <div className="col-4 p-0">
                        <div className="" style={{
                          fontSize: '15px',
                          color: theme === 'dark' ? 'rgba(255,255,255,.6)' : ''
                        }}>
                          Fund
                        </div>
                        <div className="" style={{
                          fontSize: '22px',
                          fontWeight: '500',
                          letterSpacing: '.02em',
                          color: theme === 'dark' ? 'var(--theme-light-secondary)': "",
                        }}>
                          {activeRequestData?.convert_fund_code}
                        </div>
                        <div className="text-truncate"
                          data-tooltip-id={"convert-fund-name"}
                          data-tooltip-content={activeRequestData?.convert_fund_name}
                        style={{
                          cursor: 'pointer',
                          fontSize: '13px',
                          lineHeight: '1.1',
                          maxWidth: '70%'
                        }}>
                          {activeRequestData?.convert_fund_name}
                        </div>
                            <Tooltip
                              // anchorSelect={"asd"}
                              id={"convert-fund-name"}
                              place='bottom'
                              // offset={12}
                              // delayShow={120}
                              className='my-rt'
                              classNameArrow='my-rt-arrow'
                              border='1px solid rgba(255,255,255,0.12)'
                              arrowSize={8}
                              opacity={0.95}
                            />
                      </div>
                      <div className="col-4 p-0">
                        <div className="" style={{
                          fontSize: '15px',
                          color: theme === 'dark' ? 'rgba(255,255,255,.6)' : ''
                        }}>
                          Price
                        </div>
                        <div className="" style={{
                          fontSize: '22px',
                          fontWeight: '500',
                          letterSpacing: '.02em',
                          color: theme === 'dark' ? 'var(--theme-light-secondary)': ""
                        }}>
                          <span dir="ltr" className="" style={{ marginRight: '5px', fontSize: '15px', fontWeight: '400', color: theme === 'dark' ? '#b2cbdb' : '' }}>
                            {t("app.fund.rupee")}
                          </span>
                          {numberWithCommas(conversionRequest?.convert_price?.whole)}
                          {conversionRequest?.convert_price?.hasDecimal ? "." : ""}
                          {conversionRequest?.convert_price?.hasDecimal ? (
                            <span className="" style={{ fontSize: '16px' }}>
                              {conversionRequest?.convert_price?.decimal}
                            </span>
                          ) : ""}
                        </div>
                        <div className="text-truncate" style={{
                          fontSize: '13px',
                          lineHeight: '1.1'
                        }}>
                          {" "}
                        </div>
                      </div>
                      <div className="col-4 p-0">
                        <div className="" style={{
                          fontSize: '15px',
                          color: theme === 'dark' ? 'rgba(255,255,255,.6)' : ''
                        }}>
                          Units Converted
                        </div>
                        <div className="" style={{
                          fontSize: '22px',
                          fontWeight: '500',
                          letterSpacing: '.02em',
                          color: theme === 'dark' ? 'var(--theme-light-secondary)': ""
                        }}>
                          {numberWithCommas(conversionRequest?.convert_units?.whole)}
                          {conversionRequest?.convert_units?.hasDecimal ? "." : ""}
                          {conversionRequest?.convert_units?.hasDecimal ? (
                            <span className="" style={{ fontSize: '16px' }}>
                              {conversionRequest?.convert_units?.decimal}
                            </span>
                          ) : ""}
                          <span dir="ltr" className="" style={{ marginLeft: '5px', fontSize: '15px', fontWeight: '400', color: theme === 'dark' ? '#b2cbdb' : '' }}>
                            {t("app.fund.units")}
                          </span>
                        </div>
                        <div className="text-truncate" style={{
                          fontSize: '13px',
                          lineHeight: '1.1'
                        }}>
                          {" "}
                        </div>
                      </div>
                    </div>
                  </div>
              </div>



              {/* {activeRequestData?.request_status !== "PAYMENT-PENDING" ? (
                            ) : (
                            )} */}
            </div>

            <div
              className='modal-footer justify-content-center border-0 flex-nowrap'
              style={{
                justifyContent: allowedExplicitActions.includes(activeRequestData?.request_status) ? "space-between" : "center",
              }}
            >
              <button
                type='button'
                className='btn border-0 px-4 pb-2'
                onClick={() => {
                  if (requestDetailsModal.current) {
                    requestDetailsInstanceModal.current?.toggle();
                  }
                }}
                style={{
                  ...customModalCancelButton,
                  minWidth: "94px",
                }}
              >
                {t("modals.common.close")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {!isFetching ? <Pagination totalPages={investorRequests?.totalPages} activePageNumber={activePageNumber} onChange={setActivePageNumber} theme={theme} /> : ""}
    </div>
  );
};

export default Requests;
